// LO QUE CUELGA DE UNA PARED: la máquina del eléctrico.
//
// Lo pidió Daniel el 9-oct-2026 pensando en otro flujo —«las reuniones
// iniciales del departamento de arquitectura con el cliente»—: poder marcar
// tomacorrientes, apagadores y luces encima de la planta mientras se conversa.
// Y marcó el comportamiento con precisión:
//
//   «ciertos objetos, como son tomacorrientes, apagadores, tengan un
//    comportamiento que sea un poco similar a las puertas y ventanas, que
//    básicamente se adhieren, pero en este caso se vería o hacia el lado
//    externo o interno de la pared, que por defecto tenga ese comportamiento,
//    pero que si uno desea puede ponerlo en el centro de cualquier área o en
//    algún punto que uno defina que no sea la pared. En el tema de luces, eso
//    iría más bien directamente en los espacios, eso no tiene el comportamiento
//    de paredes.»
//
// Esas tres frases son todo este archivo:
//
//   · `anclaCerca` resuelve el «se adhieren»: a qué pared y por qué cara.
//   · Que devuelva `null` lejos de toda pared resuelve el «o en algún punto que
//     uno defina»: no hace falta un modo ni una bandera, soltar la pieza en
//     medio del cuarto ya la deja suelta.
//   · `pegaPared` en el catálogo resuelve el «las luces no»: la luz nunca
//     pregunta por una pared.
//
// Vive aparte de `muebles.tsx` por lo que mide cada uno: aquél dibuja, éste
// calcula geometría. Y aparte de `EditorPlanta.tsx` porque es lo único de esta
// función que se puede probar sin un navegador.

import type { AnclaPared, Mueble, Pared, Planta } from './tipos';
import { CATALOGO, fichaMueble } from './muebles';

/** Al milímetro, que es la unidad que guarda todo el editor. */
const mm = (v: number) => Math.round(v * 1000) / 1000;

/**
 * Cuánto se puede alejar el dedo de una pared y que la pieza igual se pegue, en
 * metros.
 *
 * 40 cm es ancho a propósito: el gesto que uno hace es «poné un toma en esta
 * pared», no «poné un toma en esta coordenada», y el dedo tapa el muro justo
 * cuando lo está apuntando. Lejos de ahí manda la otra mitad del pedido —la
 * pieza se queda donde se soltó— así que pasarse de generoso tampoco encierra
 * a nadie: basta soltarla más al centro del cuarto.
 */
export const ALCANCE_PARED = 0.4;

/** La pared de un ancla, o `undefined` si esa pared ya no existe. */
export const paredDeAncla = (ps: Pared[], a: AnclaPared) =>
  ps.find((p) => p.id === a.pared);

/**
 * Dónde cae en el PLANO una pieza colgada de una cara, y hacia dónde mira.
 *
 * La pieza se apoya con la espalda EXACTAMENTE sobre la cara del muro: el
 * símbolo eléctrico no es el aparato —ver `TAM_ELECTRICO`— y separarlo un par
 * de centímetros «para que se vea» sólo lograría que a un zoom se despegue.
 *
 * El giro sale de la cara y no se elige: la espalda del dibujo es su norte
 * local (ver los símbolos en `muebles.tsx`), así que pegarse a un muro que
 * queda al este es girar 90°, al sur 180°, al oeste 270°. Por eso una pieza
 * anclada no ofrece el botón de rotar: su giro lo manda la pared.
 */
export function asientoDeAncla(p: Pared, a: AnclaPared, m: Mueble) {
  // El `h` local SIEMPRE es lo que la aleja de la cara y el `w` lo que la corre
  // a lo largo, en los dos tipos de pared: la pieza gira CON el muro, así que
  // su espalda —su norte, ver los símbolos— queda contra la cara en los dos
  // casos. Mirar la orientación de la pared para elegir cuál es cuál sería
  // mirarla dos veces, y en una pieza que no fuera cuadrada daría al revés.
  const fondo = m.h;
  const largo = m.w;
  // Nunca a caballo de una punta: una pieza que asoma media fuera de la pared
  // se lee como un error de dibujo. Si la pared es más corta que la pieza —no
  // pasa con 20 cm, pero un muro de 15 cm existe— se centra y se acepta.
  const centro = p.b - p.a <= largo
    ? (p.a + p.b) / 2
    : Math.min(Math.max(a.t, p.a + largo / 2), p.b - largo / 2);
  const eje = p.c + a.cara * (p.e / 2 + fondo / 2);
  const giro: Mueble['giro'] = p.o === 'v'
    ? (a.cara === -1 ? 90 : 270)
    : (a.cara === -1 ? 180 : 0);
  return p.o === 'v'
    ? { x: mm(eje - m.w / 2), y: mm(centro - m.h / 2), t: mm(centro), giro }
    : { x: mm(centro - m.w / 2), y: mm(eje - m.h / 2), t: mm(centro), giro };
}

/**
 * La pared más cercana al punto, y por qué cara. `null` si no hay ninguna a
 * tiro — y eso NO es un fallo: es la pieza puesta suelta.
 *
 * La cara la decide DE QUÉ LADO está el dedo, que es lo que uno acaba de
 * señalar. En la práctica eso es «hacia adentro» sin tener que medir nada:
 * quien arrastra un toma a la pared de un dormitorio tiene el dedo dentro del
 * dormitorio. Cuando el dedo cae justo sobre el eje —ni de un lado ni del
 * otro— se elige `+1` por desempate, y el botón de voltear de la paleta
 * resuelve el caso raro en un toque.
 */
export function anclaCerca(
  ps: Pared[], px: number, py: number, alcance = ALCANCE_PARED,
): AnclaPared | null {
  let mejor: { d: number; a: AnclaPared } | null = null;
  for (const p of ps) {
    const perp = p.o === 'v' ? px : py;
    const alo = p.o === 'v' ? py : px;
    // Sólo cuenta la pared que uno está señalando, no su recta infinita: la
    // prolongación de un muro del baño cruza media casa.
    if (alo < p.a - alcance || alo > p.b + alcance) continue;
    const d = Math.abs(perp - p.c) - p.e / 2;
    if (d > alcance) continue;
    if (mejor && d >= mejor.d) continue;
    const cara: 1 | -1 = perp < p.c ? -1 : 1;
    mejor = { d, a: { pared: p.id, t: mm(alo), cara } };
  }
  return mejor?.a ?? null;
}

/** ¿Esta pieza del catálogo nace pegada a la pared? */
export const pegaPared = (tipo: string) => fichaMueble(tipo)?.pegaPared === true;

/** ¿Esta pieza es una salida eléctrica? */
export const esElectrica = (tipo: string) => fichaMueble(tipo)?.electrico === true;

/** La altura de montaje que corresponde mostrar: la propia, o la de fábrica. */
export const alturaDe = (m: Mueble) =>
  m.altura ?? fichaMueble(m.tipo)?.alturaDefecto ?? null;

/**
 * Vuelve a sentar sobre su pared a todo lo que esté anclado. Devuelve `true` si
 * movió algo.
 *
 * Es lo que hace que «adherido» signifique algo: sin esto, mover el muro dejaría
 * los tomacorrientes flotando donde estaba el muro antes, y habría que
 * reacomodarlos a mano uno por uno después de cada ajuste —que es exactamente
 * el trabajo que esta pantalla existe para no hacer—.
 *
 * Una pieza cuya pared YA NO EXISTE no se borra: se le quita el ancla y se
 * queda donde estaba, que es la otra mitad legítima del pedido. Borrar una
 * pared no puede borrar en silencio lo que alguien marcó encima.
 */
export function reasentarAnclados(planta: Planta): boolean {
  let tocado = false;
  for (const m of planta.muebles ?? []) {
    if (!m.ancla) continue;
    const p = paredDeAncla(planta.paredes, m.ancla);
    if (!p) { delete m.ancla; tocado = true; continue; }
    const s = asientoDeAncla(p, m.ancla, m);
    if (m.x === s.x && m.y === s.y && m.giro === s.giro && m.ancla.t === s.t) continue;
    m.x = s.x; m.y = s.y; m.giro = s.giro; m.ancla.t = s.t;
    tocado = true;
  }
  return tocado;
}

/**
 * Cuántas piezas eléctricas hay de cada tipo Y ALTURA en TODA la casa, no en la
 * planta a la vista.
 *
 * Lo decidió Daniel así —«total de la casa, una línea por tipo de pieza»— y es
 * lo que corresponde a para qué sirve el número: lo que se cotiza y lo que se
 * conversa con el cliente es la casa, y nadie pide «doce tomas en la planta
 * alta».
 *
 * La ALTURA entra en la llave y no como un detalle que se despliega, que fue la
 * otra opción que puso sobre la mesa el 9-oct-2026 («que se agrupen por altura
 * o que se pueda expandir»). Agrupar es más simple y es además como se lee un
 * cuadro eléctrico de verdad: «6 tomas a 0,30 / 2 tomas a 1,10» es la línea que
 * se le pasa al electricista, porque la altura cambia la instalación. Y
 * mientras nadie mueva una pieza de su altura de fábrica —el caso normal— la
 * tabla tiene exactamente las mismas filas que antes, sólo que rotuladas.
 *
 * Ordenado por el catálogo y no por cantidad: una lista que se reordena sola
 * cada vez que se agrega una pieza obliga a volver a leerla entera.
 */
export function resumenElectrico(plantas: Planta[]) {
  const cuenta = new Map<string, { tipo: string; altura: number | null; cantidad: number }>();
  for (const pl of plantas) {
    for (const m of pl.muebles ?? []) {
      if (!esElectrica(m.tipo)) continue;
      const altura = alturaDe(m);
      const llave = `${m.tipo}|${altura ?? ''}`;
      const ya = cuenta.get(llave);
      if (ya) ya.cantidad += 1;
      else cuenta.set(llave, { tipo: m.tipo, altura, cantidad: 1 });
    }
  }
  return [...cuenta.values()].map((f) => ({
    ...f,
    nombre: fichaMueble(f.tipo)?.nombre ?? f.tipo,
    // El orden del catálogo. Lo que el usuario se guardó como pieza propia no
    // está en `CATALOGO` y cae al final, que es donde se lo espera.
    orden: (i => (i < 0 ? CATALOGO.length : i))(CATALOGO.findIndex((c) => c.clave === f.tipo)),
  })).sort((a, b) => a.orden - b.orden
    || a.nombre.localeCompare(b.nombre, 'es')
    || (a.altura ?? 0) - (b.altura ?? 0));
}
